import { useEffect } from 'react';
import {
    Outlet,
    useLoaderData,
    useMatches,
    useSearchParams,
} from 'react-router';
import { useSetAtom } from 'jotai';
import type { UIMatch } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import type { APHandle } from '~/modules/root';
import PageLayout from '@modules/professional/component/page-layout/PageLayout';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import { ProfessionalHeroSection } from '~/ui-rocco/pages/professional-detail/sections/ProfessionalHeroSection';
import { DetailMobileAppChrome } from '~/ui-rocco/pages/detail-shared';
import { ProfessionalProfileHero } from '~/ui-rocco/pages/professional-detail/sections/ProfessionalProfileHero';
import { ProfessionalProfileHeader } from '~/ui-rocco/pages/professional-detail/sections/ProfessionalProfileHeader';
import { ProfessionalSubNav } from '~/ui-rocco/pages/professional-detail/sections/ProfessionalSubNav';
import { ProfessionalDesktopSubNav } from '~/ui-rocco/pages/professional-detail/sections/ProfessionalDesktopSubNav';
import { ProfessionalDetailsMagicIsland } from '~/ui-rocco/pages/professional-detail/magic-island/ProfessionalDetailsMagicIsland';
import { ProfessionalIslandFallback } from '~/ui-rocco/pages/professional-detail/magic-island/ProfessionalIslandFallback';
import {
    PermissionEnum,
    type ProfessionalDetailsInitialFragment,
} from 'generated/graphql';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';
import type { Breadcrumb } from '@modules/professional/type/details-root.types';
import type { DropdownItem } from '@modules/enquiry/types';
import { useInView } from 'react-intersection-observer';
import { useSetEditMode } from '~/modules/root/hook/use-set-edit-mode';
import { useEditPermissions, usePermissions } from '~/modules/user';
import type { PrimaryCTAResult } from '@modules/professional/util/professional-cta-helper';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';
import type { ProfessionalHeaderSignals } from '@modules/professional/hook/use-professional-initial-route-data';
import type { loader } from '@modules/professional/page/loaders/professional-page-loader.server';
import type { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';

export interface ProfessionalDetailRootData {
    initial: ProfessionalDetailsInitialFragment;
    professionalBreadcrumb: Breadcrumb[];
    banners: ProfessionalBannersData;
    branchOptions: DropdownItem<Branch>[];
    localisation: Awaited<ReturnType<typeof getPageLocalisation>>;
    primaryCTA: PrimaryCTAResult;
    headerSignals: ProfessionalHeaderSignals;
    shouldIncludeLocalBusinessSchema: boolean;
    showHDEExhibitorMark: boolean;
}

export type ProfessionalDetailRootLoaderData = typeof loader;

const ProfessionalDetailRootPage = () => {
    const { initial, primaryCTA, awards, detailsRevamp } =
        useLoaderData<ProfessionalDetailRootLoaderData>();
    const { desktop } = useAppDisplayModeContext();
    const { canEditProfile } = useEditPermissions(initial.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmAnalyticsView,
        initial.ID,
    );
    const editLink = getBMPagePath(initial.URLSegment);
    useSetEditMode(editLink, canEditProfile);
    const [footerSectionRef, footerSectionInView] = useInView({
        threshold: 0.5,
    });

    const setCountryData = useSetAtom(setCloneCountryStateAtom);
    const { setCategory } = useCurrentCategory();

    // Set the atom value when the data loads
    useEffect(() => {
        setCountryData({
            urlSegment: initial?.URLSegment,
            currentCountry: initial?.Country,
            pageType: initial?.PageType,
            associatedPages: initial?.AssociatedPages,
        });
    }, [initial, setCountryData]);

    useEffect(() => {
        setCategory('Professionals');
        return () => setCategory(null);
    }, [setCategory]);

    const matches = useMatches();
    const shouldSkipLayout = matches.some(
        (match) =>
            (match as UIMatch<unknown, APHandle>)?.handle
                ?.skipProfessionalLayout === true,
    );

    // The BC-4505/BC-4572 editorial revamp owns the whole page, so skip the
    // legacy banner + tab chrome when its flag is on for any route that renders
    // the revamp layout — the overview and the revamped tabs (e.g. /journal).
    // (?revamp=1 forces it in local dev.)
    const [searchParams] = useSearchParams();
    const ownsRevampLayout = matches.some((match) => {
        const matchHandle = (match as UIMatch<unknown, APHandle>)?.handle;
        return (
            matchHandle?.isOverviewPage === true ||
            matchHandle?.professionalRevampLayout === true
        );
    });
    const showRevamp =
        ownsRevampLayout &&
        (detailsRevamp ||
            (import.meta.env.DEV &&
                (searchParams.get('revamp') === '1' ||
                    searchParams.get('detailsRevamp') === '1')));

    // If a child route requests to skip the professional layout (or the revamp
    // is active), render just the Outlet — but the revamp still needs the BM
    // admin buttons (edit-mode toggle + copy link) that PageLayout would render.
    if (shouldSkipLayout || showRevamp) {
        const content = (
            <>
                {showRevamp ? (
                    <>
                        <DetailPageButtons
                            id={initial.ID}
                            editLink={editLink}
                            shareableLink={`/professional/${initial.URLSegment}`}
                            canEdit={canEditProfile}
                            canCopyLink={canCopyLink}
                        />
                        {/* Mounted in the persistent layout (not a tab page) so
                            the app-mode chrome survives tab navigation. */}
                        <DetailMobileAppChrome />
                        {/* The whole header — banner, identity/stats/CTA, and
                            the tab bar — lives here (not in each tab page) so it
                            persists across tab navigation and never re-flashes;
                            only the tab body below the nav swaps. Desktop keeps
                            the magazine-cover hero + pill tabs (BC-4505); the
                            social-profile layout is mobile-only. */}
                        {desktop ? (
                            <>
                                <ProfessionalHeroSection />
                                <ProfessionalDesktopSubNav />
                            </>
                        ) : (
                            <>
                                <ProfessionalProfileHero />
                                <ProfessionalProfileHeader />
                                <ProfessionalSubNav />
                            </>
                        )}
                    </>
                ) : null}
                <Outlet />
            </>
        );

        // The revamp mounts a persistent Magic Island here (not per tab) so the
        // enquiry pill survives tab navigation. The fallback owns the pill; tab
        // sections can still claim the island as they scroll into view.
        return showRevamp ? (
            <ProfessionalDetailsMagicIsland>
                <ProfessionalIslandFallback />
                {content}
            </ProfessionalDetailsMagicIsland>
        ) : (
            content
        );
    }

    return (
        <PageLayout
            primaryCTA={primaryCTA}
            awards={awards}
            canCarouselControl={canEditProfile}
        >
            <DetailPageButtons
                id={initial.ID}
                editLink={editLink}
                shareableLink={`/professional/${initial.URLSegment}`}
                canEdit={canEditProfile}
                canCopyLink={canCopyLink}
                showButton={!footerSectionInView}
            />
            <Outlet />
            <div ref={footerSectionRef} />
        </PageLayout>
    );
};

export default ProfessionalDetailRootPage;
